import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, ArrowLeft, Download, ShoppingBag, Camera, Sparkles } from "lucide-react";
import MemberShell from "@/components/MemberShell";
import { authHeaders } from "@/lib/auth";
import { useT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /member/orders — kit + shoot order history with download links for paid items.
 *
 * Two kinds rendered in one merged timeline:
 *   - kind="kit"   → digital AI Content Growth Kit purchases (Gumroad-fulfilled)
 *   - kind="shoot" → shoot bookings (50% deposit + 50% balance flow)
 */
export default function MemberOrders() {
    const { lang } = useT();
    const isCn = lang === "cn";
    const nav = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [me, list] = await Promise.all([
                    axios.get(`${API}/auth/me`, { headers: authHeaders() }),
                    axios.get(`${API}/member/hub/kit-orders`, { headers: authHeaders() }),
                ]);
                if (cancelled) return;
                setEmail(me?.data?.member?.email || "");
                setOrders(Array.isArray(list?.data) ? list.data : []);
            } catch (err) {
                if (err?.response?.status === 401) {
                    nav("/member/login?next=/member/orders", { replace: true });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [nav]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-cafe-cream">
                <Loader2 className="h-8 w-8 animate-spin text-cafe-olive" />
            </div>
        );
    }

    return (
        <MemberShell email={email}>
            <Link to="/member" className="inline-flex items-center text-cafe-olive hover:text-cafe-oliveDark text-sm mb-6" data-testid="orders-back">
                <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> {isCn ? "返回 Hub" : "Back to Hub"}
            </Link>
            <h1 className="font-display text-3xl md:text-4xl text-cafe-espresso mb-2 leading-tight">
                {isCn ? "我的订单" : "My orders"}
            </h1>
            <p className="text-cafe-mocha text-sm mb-8">
                {isCn ? "Kit 与拍摄订单历史 · 已付款的可重新下载文件。" : "Kit purchases and shoot bookings · re-download paid files anytime."}
            </p>

            {orders.length === 0 ? (
                <div className="text-cafe-mocha/70 py-16 text-center bg-white/60 border border-dashed border-cafe-espresso/15 rounded-2xl" data-testid="orders-empty">
                    <ShoppingBag className="h-8 w-8 mx-auto mb-4 text-cafe-mocha/40" />
                    <p className="text-sm italic mb-3">{isCn ? "暂无订单。" : "No orders yet."}</p>
                    <Link to="/ig/content/AI" className="text-xs text-cafe-olive hover:underline">{isCn ? "→ 浏览 IG Content AI" : "→ Browse IG Content AI"}</Link>
                </div>
            ) : (
                <div className="space-y-3" data-testid="orders-list">
                    {orders.map((o, idx) => {
                        const isShoot = o.kind === "shoot";
                        const totalRm = isShoot ? (o.total_cents || 0) / 100 : (o.amount_total || 0) / 100;
                        const Icon = isShoot ? Camera : ShoppingBag;
                        return (
                            <div
                                key={o.id || idx}
                                data-testid={`order-row-${o.id || idx}`}
                                className="bg-cafe-cream border border-cafe-espresso/10 rounded-2xl p-5"
                            >
                                <div className="flex items-start justify-between gap-3 mb-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <Icon className="h-3.5 w-3.5 text-cafe-olive" />
                                            <span className="text-[10px] tracking-[0.22em] uppercase text-cafe-olive font-semibold">
                                                {isShoot ? (isCn ? "拍摄订单" : "Shoot booking") : (isCn ? "Kit 订单" : "Kit purchase")}
                                            </span>
                                        </div>
                                        <h3 className="font-display text-lg text-cafe-espresso leading-tight">
                                            {isShoot
                                                ? (isCn ? (o.package_name_cn || o.package_name_en) : o.package_name_en)
                                                : (o.tier_label || o.package_id || "Order")}
                                        </h3>
                                        <div className="text-xs text-cafe-mocha mt-0.5">
                                            {o.created_at ? new Date(o.created_at).toLocaleDateString("en-MY", { year: "numeric", month: "short", day: "numeric" }) : ""}
                                        </div>
                                        {o.id && (
                                            <div className="text-[11px] text-cafe-mocha/80 mt-1 font-mono" data-testid={`order-number-${o.id}`} title={o.id}>
                                                {isCn ? "订单编号" : "Order No."}: #{String(o.id).slice(0, 8).toUpperCase()}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <div className="font-display text-lg text-cafe-espresso tabular-nums">
                                            RM {totalRm.toFixed(2)}
                                        </div>
                                        <StatusPill status={o.status} isCn={isCn} isShoot={isShoot} />
                                    </div>
                                </div>

                                {/* Action row: download if paid + delivery_url present */}
                                <div className="flex flex-wrap gap-2 pt-3 border-t border-cafe-espresso/10">
                                    {!isShoot && ["delivered", "fulfilled"].includes(o.status) && (
                                        <Link
                                            to={`/member/kit/${o.id}`}
                                            data-testid={`order-open-kit-${o.id}`}
                                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cafe-olive text-cafe-cream text-xs font-bold hover:bg-cafe-oliveDark transition-colors"
                                        >
                                            <Sparkles className="h-3 w-3" /> {isCn ? "打开我的内容" : "Open my content"}
                                        </Link>
                                    )}
                                    {isShoot && o.status === "paid_full" && o.delivery_files_url && (
                                        <a
                                            href={o.delivery_files_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`order-download-${o.id}`}
                                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cafe-espresso text-cafe-cream text-xs hover:bg-[#1A0F0A] transition-colors"
                                        >
                                            <Download className="h-3 w-3" /> {isCn ? "下载文件" : "Download files"}
                                        </a>
                                    )}
                                    {!isShoot && o.delivery_url && (
                                        <a
                                            href={o.delivery_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`order-kitdownload-${o.id}`}
                                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cafe-espresso text-cafe-cream text-xs hover:bg-[#1A0F0A] transition-colors"
                                        >
                                            <Download className="h-3 w-3" /> {isCn ? "下载 Kit" : "Download kit"}
                                        </a>
                                    )}
                                    {isShoot && (o.status === "delivered" || o.status === "balance_email_sent") && (
                                        <span className="text-xs text-cafe-mocha italic">
                                            {isCn ? `尾款待付 · RM ${(o.balance_cents / 100).toFixed(2)}` : `Balance due · RM ${(o.balance_cents / 100).toFixed(2)}`}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </MemberShell>
    );
}


function StatusPill({ status, isCn, isShoot }) {
    const m = {
        paid_full: { en: "Paid full", cn: "已付清", c: "bg-cafe-olive text-cafe-cream" },
        deposit_paid: { en: "Deposit paid", cn: "定金已付", c: "bg-cafe-olive/20 text-cafe-olive" },
        delivered: isShoot
            ? { en: "Delivered · balance due", cn: "已交付 · 待付尾款", c: "bg-[#D4A574]/20 text-[#B88A55]" }
            : { en: "Delivered", cn: "已交付", c: "bg-cafe-olive text-cafe-cream" },
        balance_email_sent: { en: "Balance due", cn: "待付尾款", c: "bg-cafe-terracotta/15 text-cafe-terracotta" },
        paid: { en: "Paid", cn: "已付款", c: "bg-cafe-olive text-cafe-cream" },
        fulfilled: { en: "Delivered", cn: "已交付", c: "bg-cafe-olive text-cafe-cream" },
        shipped: { en: "Shipped", cn: "已发货", c: "bg-cafe-olive text-cafe-cream" },
    }[status] || { en: status, cn: status, c: "bg-cafe-mocha/15 text-cafe-mocha" };

    return (
        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.c}`}>
            {isCn ? m.cn : m.en}
        </span>
    );
}
